import React, { useEffect, useState } from 'react';

import './transactions-filter.scss';
import { yupDateTransformer } from 'utilities/dayjs-utilities';
import {
  Form,
  Button as AmpolButton,
  ButtonVariant,
  ButtonColor,
  ButtonType,
  ReadonlyField,
  FormField,
} from '@ampol/design-system';
import { useSelector } from 'react-redux';
import Validation from 'constants/validation-messages';
import Regex from 'constants/regex';
import * as yup from 'yup';
import DeprecatedIcon from 'assets/svg-icons';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';

export interface FilterProps {
  contractId: string;
  cardNo: string;
  vehicleRegNo: string;
  driverName: string;
  sourceNo: string;
  startDate: string;
  endDate: string;
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: boolean;
  isFilterClick: boolean;
  triggerFilter: boolean;
}

// Props
interface TransactionsFilterProps {
  modalType: string;
  filterValues: FilterProps;
  // eslint-disable-next-line no-unused-vars
  setFilterValues: (value: React.SetStateAction<FilterProps>) => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (event: any) => void;
  resetFilterValues: () => void;
  accountLevel: string;
  toggleFilter?: () => void;
}
const TransactionsFilter = ({
  modalType,
  filterValues,
  setFilterValues,
  onSubmit,
  resetFilterValues,
  accountLevel,
}: TransactionsFilterProps) => {
  const allAccountsLoading = useSelector((state: any) => state.accountsStore.isAllAccountsLoading);
  const allAccounts = useSelector((state: any) => state.accountsStore.allAccounts);

  const [accountOptions, setAccountOptions] = useState<undefined | any[]>(undefined);

  const isAccountNumberDisabled = !!modalType;
  const isCardNumberDisabled = (modalType === 'account' && accountLevel !== 'C') || modalType === 'card';

  const validationSchema = yup.object().shape({
    contractId: yup.string().test('conditional-required', Validation.ACCOUNT_OR_CARD_NUMBER_REQUIRED, (value) => {
      const hasAccountNo = value !== '';
      const hasCardNo = filterValues?.cardNo !== '';

      return hasAccountNo || hasCardNo;
    }),
    cardNo: yup
      .string()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value;
      })
      .nullable()
      .test('conditional-required', Validation.ACCOUNT_OR_CARD_NUMBER_REQUIRED, (value) => {
        const hasCardNo = value !== null;
        const hasAccountNo = filterValues?.contractId !== ''; // if ADS DropdownSearch had name as an id, could use parent.contractId.value and remove handleChange

        return hasCardNo || hasAccountNo;
      })
      .length(16, Validation.EXACT_DIGITS_SIXTEEN)
      .matches(Regex.ONLY_NUMBERS, Validation.ONLY_NUMBERS),
    vehicleRegNo: yup
      .string()
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(12, Validation.MAX_CHAR_TWELVE),
    driverName: yup
      .string()
      .matches(Regex.DRIVER_NAME, Validation.DRIVER_NAME)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    sourceNo: yup.string().matches(Regex.LOCATION_NUMBER_OPTIONAL, Validation.LOCATION_NUMBER),
    startDate: yup.date().nullable().required(Validation.START_DATE_REQUIRED).transform(yupDateTransformer),
    endDate: yup
      .date()
      .nullable()
      .required(Validation.END_DATE_REQUIRED)
      .transform(yupDateTransformer)
      .min(yup.ref('startDate'), Validation.END_DATE_AFTER_START),
  });

  useEffect(() => {
    if (!modalType && allAccounts) {
      computeAccountOptions(allAccounts);
    }
  }, [allAccounts]);

  /**
   * Get hierarchy of accounts for Transaction Filter Dropdown data
   * @param {Array} accounts
   */
  const computeAccountOptions = (accounts) => {
    const getOption = (item) => {
      const children = groupDescendants(item.contractNumber);
      return {
        label: item.contractNumber + ' ' + item.contractName,
        value: children.length > 0 ? children : item.contractNumber,
      };
    };

    const groupDescendants = (parentId = '') => {
      return accounts.filter((item) => item.parent === parentId).map((item) => getOption(item));
    };

    const results = [];
    const rootAccounts = accounts.filter((account) => !accounts.map((a) => a.contractNumber).includes(account.parent));

    rootAccounts.forEach((item) => {
      results.push(getOption(item));
    });

    setAccountOptions(results);

    if (accounts.length === 1) {
      setFilterValues({
        ...filterValues,
        contractId: accounts[0].contractNumber,
        cardNo: '',
      });
    }
  };

  const handleChange = (values) => {
    // Required function to get validation schema to onChange to work
    setFilterValues({
      ...filterValues,
      ...values,
    });
  };

  const handleLocationNumberBlurred = () => {
    if (filterValues.sourceNo.match(Regex.LOCATION_NUMBER_PARTIAL)) {
      setFilterValues({
        ...filterValues,
        sourceNo: '000' + filterValues.sourceNo,
      });
    }
  };

  return (
    <div data-testid='transactions-filter'>
      <Form
        id={'transactions-filters'}
        schema={validationSchema}
        defaultValues={filterValues}
        onSubmit={onSubmit}
        onChange={handleChange}
      >
        <div className={`transaction-filters__grid ${modalType && 'transaction-filters__grid--modal'}`}>
          <span className={'transaction-filters__spacer'}></span>
          <span className={'transaction-filters__spacer'}></span>
          <span className={'transaction-filters__subtitle1'}>Transaction date (range)</span>
          {isAccountNumberDisabled && (
            <FormField
              label='Account number'
              name='contractId'
              forceFloating
              render={(onChange, onBlur, value) => <ReadonlyField type='dropdown' key='contractId' value={value} />}
            />
          )}
          {!isAccountNumberDisabled && (
            <Form.DropdownSearch
              type={'dropdown'}
              label={'Account number'}
              name={'contractId'}
              className={'transaction-filters__flex-item'}
              loading={allAccountsLoading}
              filterOptions
              data={accountOptions}
            />
          )}
          <Form.Input
            label={'Vehicle registration'}
            name={'vehicleRegNo'}
            className={'transaction-filters__flex-item transaction-filters__uppercase'}
          />
          <div
            className={`transaction-filters__grid-col-span-1 ${modalType && 'transaction-filters__grid-col-span-1--modal'}`}
          >
            <div className={'transaction-filters__flex-item'}>
              <Form.DatePicker
                placeholder={'dd/mm/yyyy'}
                name={'startDate'}
                label={'Transaction date from'}
                inputSuffix={
                  <SuffixWrapper>
                    <DeprecatedIcon iconName='calender' />
                  </SuffixWrapper>
                }
                getPopupContainer={(trigger) => (modalType ? document.body : trigger.parentElement)}
              />
            </div>
            <span
              className={`transaction-filters__range-splitter ${modalType && 'transaction-filters__range-splitter--modal'}`}
            />
            <div className={'transaction-filters__flex-item'}>
              <Form.DatePicker
                placeholder={'dd/mm/yyyy'}
                name={'endDate'}
                label={'Transaction date to'}
                inputSuffix={
                  <SuffixWrapper>
                    <DeprecatedIcon iconName='calender' />
                  </SuffixWrapper>
                }
                getPopupContainer={(trigger) => (modalType ? document.body : trigger.parentElement)}
              />
            </div>
          </div>
          <Form.Input label={'Card number'} name={'cardNo'} disabled={isCardNumberDisabled} />
          <Form.Input label={'Driver name'} name={'driverName'} className={'transaction-filters__uppercase'} />
          <Form.Input label={'Location number'} name={'sourceNo'} onBlurCapture={handleLocationNumberBlurred} />
          <div className='filter-confirmation'>
            <AmpolButton
              variant={ButtonVariant.Secondary}
              color={ButtonColor.Blue}
              label='Clear All'
              type={ButtonType.Reset}
              onClick={resetFilterValues}
            />
            <AmpolButton
              variant={ButtonVariant.Primary}
              color={ButtonColor.Blue}
              label='Filter'
              type={ButtonType.Submit}
              formId={'transactions-filters'}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};

export default TransactionsFilter;
